import React, { useState, useEffect, useImperativeHandle } from 'react';

import i18n from 'i18n-js';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useDispatch, useSelector } from 'react-redux';
import Switch from '@material-ui/core/Switch';
import { useFormik } from 'formik';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import useStyles from './Activation.styled';

const CustomersGroupsActivation = ({ forwardRef, currentGroup, driverAccess, onSave }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.config?.settings);
  const hasIncludeAnonymousEnabled = settings?.core?.reservation?.anonymous_search || false;

  const formik = useFormik({
    initialValues: {
      activation: driverAccess,
      includeAnonymous: currentGroup?.include_anonymous || false,
    },
    onSubmit: (values) => {
      const pristine =
        driverAccess !== values.activation ||
        values.includeAnonymous !== currentGroup?.include_anonymous;
      dispatch(
        VehiclesGroupsActions.fetchVehiclesGroupsActivation(
          {
            driver_access: values.activation,
            include_anonymous: values.includeAnonymous,
          },
          pristine
        )
      );
      onSave(true);
    },
  });

  useEffect(() => {
    if (currentGroup?.id) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupById(currentGroup.id));
    }
  }, [currentGroup?.id, dispatch]);

  useEffect(() => {
    formik.setFieldValue('activation', driverAccess);
  }, [driverAccess]);

  useEffect(() => {
    if (currentGroup) {
      formik.setFieldValue('includeAnonymous', currentGroup.include_anonymous);
    }
  }, [currentGroup]);

  useImperativeHandle(forwardRef, () => ({
    submitActivation: formik.submitForm,
  }));

  const handleActivationChange = (event) => {
    const checked = event.target.checked;
    formik.setFieldValue('activation', checked);
    if (!checked) {
      formik.setFieldValue('includeAnonymous', false);
      onSave(false);
    } else {
      onSave(true);
    }
  };

  const handleIncludeAnonymousChange = (event) => {
    const checked = event.target.checked;
    formik.setFieldValue('includeAnonymous', checked);
    if (checked) {
      formik.setFieldValue('activation', true);
    }
    onSave(true);
  };

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid
          container
          className={classes.container}
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12}>
            <Box mx={3}>
              <Box display="flex" alignItems="center" justifyContent="flex-start">
                <Switch
                  onChange={handleActivationChange}
                  checked={formik.values.activation}
                  name="activation"
                  color="primary"
                />

                <Box ml={2}>
                  <Typography variant="h6">
                    <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                      {i18n.t('vehicle.group_activation')}
                    </Box>
                  </Typography>
                </Box>
              </Box>

              <Box ml={2}>
                <Typography gutterBottom component="div">
                  <Box fontSize={14} mb={4}>
                    {i18n.t('vehicle.group_activation_description')}
                  </Box>
                </Typography>
              </Box>
              {hasIncludeAnonymousEnabled && (
                <>
                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <Switch
                      onChange={handleIncludeAnonymousChange}
                      checked={formik.values.includeAnonymous}
                      name="include_anonymous"
                      color="primary"
                    />

                    <Box ml={2}>
                      <Typography variant="h6">
                        <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                          {i18n.t('vehicle.include_anonymous')}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>

                  <Box ml={2}>
                    <Typography gutterBottom component="div">
                      <Box fontSize={14} mb={4}>
                        {i18n.t('vehicle.include_anonymous_description')}
                      </Box>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
          {Array.isArray(currentGroup?.vehicle_association_requirements) && (
            <Grid item xs={12}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="flex-start"
              >
                <Typography variant="h6">
                  <Box fontWeight="fontWeightBold" fontSize={14} mt={3}>
                    {i18n.t('vehicle.missing')}
                  </Box>
                </Typography>
                <ul className={classes.requiremmentList}>
                  {currentGroup.vehicle_association_requirements.map((requiremment, index) => (
                    <li className={classes.requiremmentItem} key={index}>
                      {requiremment}
                    </li>
                  ))}
                </ul>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomersGroupsActivation;
